<template>
  <BittsContainer no-padding class="c-sharing-settings-partners-list">
    <div
      ref="scrollContainerRef"
      class="c-sharing-settings-partners-list__scroll-area scroll-pt-[40px]"
    >
      <PopulationTabListHeader
        first-column-label="Partner Name"
        select-all-label="Select all partners"
        :header-fields="headerFields"
        :all-items-selected="allPartnersSelected"
        :some-items-selected="somePartnersSelected"
        :can-edit="canEdit"
        header-class="c-sharing-settings-partners-list__header border-b border-neutral-border rounded-tr-16 rounded-tl-16 px-4 py-8 grid grid-cols-[minmax(250px,2.5fr)_minmax(240px,1.5fr)_minmax(240px,1.5fr)_minmax(120px,1.5fr)_110px] gap-4 items-center bg-white [&>*:last-child]:justify-self-end sticky top-0 z-10 min-w-[992px]"
        @select-all="handleSelectAll"
      />
      <div
        :style="{
          height: `${totalSize}px`,
          width: '100%',
          position: 'relative',
          minWidth: '992px',
        }"
      >
        <div
          v-for="virtualRow in virtualItems"
          :key="getVirtualItemKey(virtualRow.index)"
          class="c-sharing-settings-partners-list__virtual-item"
          :class="{
            'c-sharing-settings-partners-list__virtual-item--last':
              virtualRow.index === lastItemIndex,
          }"
          :style="{
            position: 'absolute',
            top: 0,
            left: 0,
            width: '100%',
            transform: `translateY(${virtualRow.start}px)`,
          }"
        >
          <PopulationTabListSubheader
            v-if="isSubheaderAtIndex(virtualRow.index)"
            :title="getSubheaderAt(virtualRow.index).title"
            :select-all-label="
              getSubheaderSelectAllLabel(getSubheaderAt(virtualRow.index).title)
            "
            :all-items-selected="
              areAllPartnersInSectionSelected(
                getSubheaderAt(virtualRow.index).id,
              )
            "
            :some-items-selected="
              areSomePartnersInSectionSelected(
                getSubheaderAt(virtualRow.index).id,
              )
            "
            :can-edit="canEdit"
            @select-all="
              (checked: boolean) =>
                handleSubheaderSelectAll(
                  getSubheaderAt(virtualRow.index).id,
                  checked,
                )
            "
          />

          <PopulationTabPartnersRow
            v-else
            :item="getPartnerAt(virtualRow.index)"
            :selected-partner-ids-set="selectedPartnerIdsSet"
            :feed-id="feedId"
            :source-id="sourceId"
            :has-changes="hasPartnerChanges(getPartnerAt(virtualRow.index).id)"
            :can-edit="canEdit"
            :is-greenfield-sharing-blocked="isGreenfieldSharingBlocked"
            :shared-base-preset-options="sharedBasePresetOptions"
            :shared-preset-fields-data="sharedPresetFieldsData"
            @visibility-change="handleVisibilityChange"
            @checkbox-change="handleCheckboxChange"
            @preset-change="handlePresetChange"
            @open-partner-settings="handleOpenPartnerSettings"
            @restore-to-default="$emit('restore-to-default', $event)"
          />
        </div>
      </div>
    </div>
  </BittsContainer>
</template>

<script setup lang="ts">
import { BittsContainer } from '@crossbeam/bitts';

import { useVirtualizer } from '@tanstack/vue-virtual';
import { computed, ref, watch } from 'vue';

import type { PresetFieldsDataMap } from '@/composables/dataSharingHub/usePresetFieldsData';
import useSharingHubChangeTracking from '@/composables/dataSharingHub/useSharingHubChangeTracking';
import type {
  PartnerListItem,
  PartnerSharingData,
  PartnerWithSharing,
  SubheaderItem,
} from '@/composables/dataSharingHub/useSharingSettingsHub';
import type { SharingType } from '@/constants/data_shares';
import type { PresetOptionsGroup } from '@/types/data_share_presets';

import PopulationTabListHeader from './PopulationTabListHeader.vue';
import PopulationTabListSubheader from './PopulationTabListSubheader.vue';
import PopulationTabPartnersRow from './PopulationTabPartnersRow.vue';

// Extended type with current display values (including unsaved changes)
type EnrichedPartner = PartnerWithSharing & {
  currentVisibility?: SharingType;
  currentPresetId?: number | null;
};

type EnrichedPartnerListItem = EnrichedPartner | SubheaderItem;

const props = withDefaults(
  defineProps<{
    partnersList: PartnerListItem[];
    selectedPartnerIdsSet: Set<number>;
    partnerIdsToRestore?: Set<number>;
    partnerIdsWithClearedOverrides?: Set<number>;
    feedId?: number;
    sourceId?: number;
    canEdit?: boolean;
    isGreenfieldSharingBlocked?: boolean;
    sharedBasePresetOptions?: PresetOptionsGroup[];
    sharedPresetFieldsData?: PresetFieldsDataMap;
  }>(),
  {
    partnerIdsToRestore: () => new Set(),
    partnerIdsWithClearedOverrides: () => new Set(),
    feedId: undefined,
    sourceId: undefined,
    canEdit: true,
    isGreenfieldSharingBlocked: false,
    sharedBasePresetOptions: undefined,
    sharedPresetFieldsData: undefined,
  },
);

const emit = defineEmits<{
  'partner-checkbox-change': [partnerId: number, checked: boolean];
  'select-all': [checked: boolean];
  'subheader-select-all': [sectionId: string, checked: boolean];
  'visibility-change': [partnerId: number, visibility: SharingType];
  'preset-change': [partnerId: number, presetId: number | undefined];
  'has-changes-updated': [hasChanges: boolean];
  'current-values-updated': [partnerId: number, values: PartnerSharingData];
  'open-partner-settings': [
    partnerId: number,
    overridesFilter: boolean | undefined,
  ];
  'restore-to-default': [partnerId: number];
}>();
const PARTNER_ROW_HEIGHT = 57;
const SUBHEADER_ROW_HEIGHT = 40;

// Change tracking
const changeTracking = useSharingHubChangeTracking<
  number,
  PartnerSharingData
>();

const scrollContainerRef = ref<HTMLElement | null>(null);

const enrichedPartnersList = computed<EnrichedPartnerListItem[]>(() => {
  void changeTracking.changedCount.value;

  return props.partnersList.map((item): EnrichedPartnerListItem => {
    if (isSubheader(item)) return item;

    const changes = changeTracking.getChanges(item.id);

    const currentVisibility =
      changes?.visibility ?? item.sharingRule?.visibility;
    const currentPresetId =
      changes?.presetId ?? item.presetInfo?.presetId ?? null;

    return {
      ...item,
      currentVisibility,
      currentPresetId,
    };
  });
});

const virtualizer = useVirtualizer(
  computed(() => ({
    count: enrichedPartnersList.value.length,
    getScrollElement: () => scrollContainerRef.value,
    estimateSize: (index: number) => {
      const item = enrichedPartnersList.value[index];
      return item && isSubheader(item)
        ? SUBHEADER_ROW_HEIGHT
        : PARTNER_ROW_HEIGHT;
    },
    getItemKey: (index: number) => {
      const item = enrichedPartnersList.value[index];
      return item?.id ?? index;
    },
    overscan: 10,
  })),
);

const virtualItems = computed(() => virtualizer.value.getVirtualItems());
const totalSize = computed(() => virtualizer.value.getTotalSize());
const lastItemIndex = computed(() => enrichedPartnersList.value.length - 1);

function getItemAt(index: number): EnrichedPartnerListItem {
  return (
    enrichedPartnersList.value[index] ?? {
      type: 'subheader' as const,
      title: '',
      id: '',
    }
  );
}

function isSubheaderAtIndex(index: number): boolean {
  return isSubheader(getItemAt(index));
}

function getSubheaderAt(index: number): SubheaderItem {
  return getItemAt(index) as SubheaderItem;
}

function getPartnerAt(index: number): EnrichedPartner {
  return getItemAt(index) as EnrichedPartner;
}

function getVirtualItemKey(index: number): string | number {
  return getItemAt(index).id;
}

watch(
  () => props.partnersList,
  (newList) => {
    newList.forEach((item) => {
      if (!isSubheader(item)) {
        const partnerId = item.id;

        if (!changeTracking.isTracking(partnerId)) {
          changeTracking.initialize(partnerId, {
            visibility: item.sharingRule?.visibility,
            presetId: item.presetInfo?.presetId ?? null,
          });
        }
      }
    });
  },
  { immediate: true },
);

watch(
  () => changeTracking.hasAnyChanges.value,
  (hasChanges) => {
    emit('has-changes-updated', hasChanges);
  },
  { immediate: true },
);

const headerFields = computed(() => {
  return [
    {
      label: 'Sharing Level',
      tooltip:
        "Choose how much of this population's data you want to share with partners.",
    },
    {
      label: 'Field Presets',
      tooltip: 'Choose which data fields to share by default.',
    },
    {
      label: 'Partner Tags',
    },
    {
      label: 'Actions',
    },
  ];
});

function getSubheaderSelectAllLabel(title: string) {
  return title
    ? `Select all partners with ${title}`
    : 'Select all partners in this section';
}

// Get partners for a specific section (custom or default)
function getPartnersInSection(sectionId: string): number[] {
  const sectionIndex = props.partnersList.findIndex(
    (item) => isSubheader(item) && item.id === sectionId,
  );

  if (sectionIndex === -1) return [];

  const partners: number[] = [];
  for (let i = sectionIndex + 1; i < props.partnersList.length; i++) {
    const item = props.partnersList[i];
    if (!item) continue;
    if (isSubheader(item)) break; // Stop at next subheader
    partners.push(item.id);
  }

  return partners;
}

// Get all visible partner IDs (excluding subheaders)
const visiblePartnerIds = computed(() => {
  return props.partnersList
    .filter((item): item is PartnerWithSharing => !isSubheader(item))
    .map((item) => item.id);
});

// Check if all visible partners are selected
const allPartnersSelected = computed(() => {
  if (visiblePartnerIds.value.length === 0) return false;
  return visiblePartnerIds.value.every((id) =>
    props.selectedPartnerIdsSet.has(id),
  );
});

// Check if some (but not all) partners are selected (for indeterminate state)
const somePartnersSelected = computed(() => {
  if (!props.selectedPartnerIdsSet.size) return false;
  const selectedVisibleCount = visiblePartnerIds.value.filter((id) =>
    props.selectedPartnerIdsSet.has(id),
  ).length;
  return (
    selectedVisibleCount > 0 &&
    selectedVisibleCount < visiblePartnerIds.value.length
  );
});

// Selection helpers

function handleSelectAll(checked: boolean) {
  emit('select-all', checked);
}

// Check if all partners in a specific section are selected
function areAllPartnersInSectionSelected(sectionId: string): boolean {
  const partnersInSection = getPartnersInSection(sectionId);
  if (partnersInSection.length === 0) return false;
  return partnersInSection.every((id) => props.selectedPartnerIdsSet.has(id));
}

// Check if some (but not all) partners in a section are selected
function areSomePartnersInSectionSelected(sectionId: string): boolean {
  const partnersInSection = getPartnersInSection(sectionId);
  if (partnersInSection.length === 0) return false;

  const selectedCount = partnersInSection.filter((id) =>
    props.selectedPartnerIdsSet.has(id),
  ).length;

  return selectedCount > 0 && selectedCount < partnersInSection.length;
}

function handleSubheaderSelectAll(sectionId: string, checked: boolean) {
  emit('subheader-select-all', sectionId, checked);
}

function handleCheckboxChange(partnerId: number, checked: boolean) {
  emit('partner-checkbox-change', partnerId, checked);
}

function handleVisibilityChange(partnerId: number, visibility: SharingType) {
  changeTracking.updateCurrent(partnerId, { visibility });
  emit('visibility-change', partnerId, visibility);
  emit('current-values-updated', partnerId, { visibility });
}

function handlePresetChange(partnerId: number, presetId: number | undefined) {
  const presetIdValue = presetId ?? null;
  changeTracking.updateCurrent(partnerId, { presetId: presetIdValue });
  emit('preset-change', partnerId, presetId);
  emit('current-values-updated', partnerId, { presetId: presetIdValue });
}

function hasPartnerChanges(partnerId: number): boolean {
  return (
    changeTracking.hasChanges(partnerId) ||
    props.partnerIdsToRestore.has(partnerId) ||
    props.partnerIdsWithClearedOverrides.has(partnerId)
  );
}

function handleOpenPartnerSettings(
  partnerId: number,
  overridesFilter?: boolean,
) {
  emit('open-partner-settings', partnerId, overridesFilter);
}

function isSubheader(item: PartnerListItem): item is SubheaderItem {
  return 'type' in item && item.type === 'subheader';
}

// Expose to parent component
defineExpose({
  hasAnyChanges: changeTracking.hasAnyChanges,
  changedCount: changeTracking.changedCount,
  getAllChangedIds: changeTracking.getAllChangedIds,
  getAllChangedItems: changeTracking.getAllChangedItems,
  commitChanges: changeTracking.commitChanges,
  reset: changeTracking.reset,
  getChanges: changeTracking.getChanges,
  batchUpdate: changeTracking.batchUpdate,
  getTrackingCount: changeTracking.getTrackingCount,
  enrichedPartnersList,
});
</script>

<style scoped lang="pcss">
@reference "../../../css/theme.css";

.c-sharing-settings-partners-list {
  @apply overflow-hidden rounded-16 flex flex-col;
  max-height: calc(100vh - 216px);
}

.c-sharing-settings-partners-list__scroll-area {
  @apply flex-1 overflow-auto;
  min-height: 0;
  container-type: inline-size;
}

.c-sharing-settings-partners-list__virtual-item {
  @apply border-b border-neutral-border;
}

.c-sharing-settings-partners-list__virtual-item--last {
  @apply border-b-0;
}

.c-sharing-settings-partners-list__virtual-item:hover {
  @apply bg-neutral-background-weak;
}

:deep(.c-population-tab-list-header__first-col) {
  @apply sticky left-0 z-[11] bg-white;
  margin-left: -4px;
  padding-left: 16px;
}

:deep(.c-population-tab-list-subheader) {
  @apply sticky left-0 z-[1];
  width: 100cqi;
}
</style>
